// AC-944 / S2.1 — LayoutRenderer + element registry + ctx contract.
//
// Walks an ILayout (rows -> columns -> elements) into a CSS grid and renders each element via a
// registry keyed by element type. Rows are grids whose tracks come from `column.width`; below the
// responsive breakpoint (ctx.isNarrow) columns auto-stack and `hideOnMobile` columns drop. Every
// node reuses the existing ISearchElementStyle shape via elementStyleFrom, so theming is unchanged.
//
// Header and footer share this renderer. The stateful/redux-coupled elements (search, cart, account
// — AC-945) and link/linkGroup (AC-946) are provided/added later; unknown types render nothing so a
// partially-built layout is always safe.

import React from 'react';
import { ILayout, ILayoutColumn, ILayoutElement, ILayoutRow, LayoutAlign, LayoutVAlign, LayoutElementType, parseLayout } from '../Helpers/layout';
import { elementStyleFrom } from '../Helpers/searchPageConfig';
import { IStorefrontSettings } from '../../Api/Models/StorefrontSettings';
import { IHeaderLink, isExternalUrl, isNonNavigableUrl } from '../Helpers/headerLinksConfig';
import HeaderDropdown from '../Nav/HeaderDropdown';
import { SOCIAL_GLYPHS, socialsFromSettings } from '../Helpers/socialGlyphs';
import { sanitizeHtml } from '../Helpers/sanitizeHtml';
import { useIsNarrow } from '../Helpers/useIsNarrow';

// What an element renderer is given. `slots` carries host-rendered stateful elements (search / cart
// / account) so the registry stays pure and testable (AC-945 fills these from NavBar/Footer).
export interface LayoutRenderContext {
    settings: IStorefrontSettings;
    region: 'header' | 'footer';
    nav: (url: string) => void;
    isNarrow: boolean;
    L: (key: string, fallback?: string) => string;
    slots?: Partial<Record<'search' | 'cart' | 'account', React.ReactNode>>;
}

export type LayoutElementRenderer = (el: ILayoutElement, ctx: LayoutRenderContext) => React.ReactNode;

// --- CSS mapping helpers -------------------------------------------------------------------------
const justify = (a?: LayoutAlign): React.CSSProperties['justifyContent'] =>
    a === 'left' ? 'flex-start' : a === 'right' ? 'flex-end' : a === 'center' ? 'center'
        : a === 'between' ? 'space-between' : a === 'around' ? 'space-around' : undefined;
const alignItems = (v?: LayoutVAlign): React.CSSProperties['alignItems'] =>
    v === 'top' ? 'flex-start' : v === 'bottom' ? 'flex-end' : v === 'center' ? 'center' : undefined;
const track = (w?: number | string): string => (w == null ? 'auto' : typeof w === 'number' ? `${w}px` : String(w));

// --- Element registry ----------------------------------------------------------------------------
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

// A single nav link, inline (AC-946 / S2.3). With `children` it becomes a Stage-1 dropdown; else a
// plain anchor. External / new-tab / tel: / mailto: are plain anchors; internal links use ctx.nav.
function renderNavLink(link: IHeaderLink, ctx: LayoutRenderContext, style?: React.CSSProperties, key?: React.Key): React.ReactNode {
    const children = (link.children ?? []).filter((c) => (c.label && c.label.trim()) || c.iconUrl);
    // AC-960 / S5.1 — a valid `panel` layout opens a mega-menu flyout and supersedes a plain dropdown.
    const panelLayout = parseLayout(link.panel);
    if (children.length > 0 || panelLayout) {
        const panelNode = panelLayout ? <LayoutRenderer layout={panelLayout} ctx={ctx} /> : undefined;
        return <HeaderDropdown key={key} link={link} onNavigate={ctx.nav} trigger={link.trigger} triggerClassName="hc-link" triggerStyle={style} itemClassName="hc-menu-item" panel={panelNode} />;
    }
    const inner = (
        <>
            {link.iconUrl && <img src={link.iconUrl} alt="" className="hc-link-icon" />}
            {link.label && <span>{link.label}</span>}
        </>
    );
    const external = isExternalUrl(link.url);
    const nonNavigable = isNonNavigableUrl(link.url);
    if (external || link.newTab) {
        const newTab = link.newTab && !nonNavigable;
        return (
            <a key={key} className="hc-link" style={style} href={link.url || '#'} target={newTab ? '_blank' : '_self'} rel={newTab ? 'noopener noreferrer' : undefined}>
                {inner}
            </a>
        );
    }
    return (
        <a key={key} className="hc-link" style={style} href={link.url || '#'} onClick={(e) => { e.preventDefault(); if (link.url) ctx.nav(link.url); }}>
            {inner}
        </a>
    );
}

const REGISTRY: Partial<Record<LayoutElementType, LayoutElementRenderer>> = {
    spacer: (el) => <div style={{ flex: '1 1 auto', ...elementStyleFrom(el.style) }} aria-hidden="true" />,

    divider: (el) => {
        const horizontal = str(el.props?.orientation) === 'horizontal';
        const base: React.CSSProperties = horizontal
            ? { alignSelf: 'stretch', borderTop: '1px solid currentColor', width: '100%', opacity: 0.3 }
            : { alignSelf: 'stretch', borderLeft: '1px solid currentColor', opacity: 0.3 };
        return <div aria-hidden="true" style={{ ...base, ...elementStyleFrom(el.style) }} />;
    },

    text: (el) => <div style={elementStyleFrom(el.style)}>{str(el.props?.text)}</div>,

    // AC-956 / S4.3 — sanitized custom HTML escape hatch. Scripts / on* handlers / javascript: URLs
    // are stripped before render (see sanitizeHtml); never rendered raw.
    html: (el) => {
        const html = sanitizeHtml(el.props?.html);
        if (!html.trim()) return null;
        return <div className="sf-layout-html" style={elementStyleFrom(el.style)} dangerouslySetInnerHTML={{ __html: html }} />;
    },

    image: (el) => {
        const url = str(el.props?.url);
        if (!url) return null;
        return <img src={url} alt={str(el.props?.alt)} style={{ maxWidth: '100%', height: 'auto', ...elementStyleFrom(el.style) }} />;
    },

    logo: (el, ctx) => {
        const src = ctx.region === 'footer' ? ctx.settings.footerLogo : ctx.settings.headerLogo;
        if (!src) return null;
        const target = ctx.region === 'footer' ? (ctx.settings.footerLogoLink ?? '/') : (ctx.settings.headerLink ?? '/');
        return (
            <img
                src={src}
                alt="Logo"
                className="sf-layout-logo"
                style={{ height: '3em', width: 'auto', cursor: 'pointer', ...elementStyleFrom(el.style) }}
                onClick={() => ctx.nav(target)}
            />
        );
    },

    title: (el, ctx) => {
        const text = ctx.settings.title || ctx.settings.name;
        if (!text) return null;
        return (
            <span className="sf-layout-title" style={{ cursor: 'pointer', whiteSpace: 'nowrap', ...elementStyleFrom(el.style) }} onClick={() => ctx.nav('/')}>
                {text}
            </span>
        );
    },

    // AC-946 / S2.3 — a single link (props are the IHeaderLink fields) and a group of links.
    link: (el, ctx) => renderNavLink(el.props as unknown as IHeaderLink, ctx, elementStyleFrom(el.style)),

    linkGroup: (el, ctx) => {
        const links = (el.props?.links as IHeaderLink[] | undefined) ?? [];
        if (links.length === 0) return null;
        return (
            <div className="sf-layout-linkgroup" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1.25em', ...elementStyleFrom(el.style) }}>
                {links.map((l, i) => renderNavLink(l, ctx, undefined, l.id ?? i))}
            </div>
        );
    },

    // AC-954 / S4.1 — a standalone labelled dropdown menu (distinct from a `link` that has children).
    // props: { label, links: IHeaderLink[], trigger }. Reuses HeaderDropdown by synthesizing a parent
    // link whose children are the menu's links.
    menu: (el, ctx) => {
        const links = (el.props?.links as IHeaderLink[] | undefined) ?? [];
        if (links.length === 0) return null;
        const trigger = el.props?.trigger === 'click' ? 'click' : undefined;
        const parent: IHeaderLink = { label: str(el.props?.label) || 'Menu', children: links, trigger };
        return <HeaderDropdown link={parent} onNavigate={ctx.nav} trigger={trigger} triggerClassName="hc-link" triggerStyle={elementStyleFrom(el.style)} itemClassName="hc-menu-item" />;
    },

    // AC-955 / S4.2 — social icons and a contact block, both sourced from existing footer* settings.
    social: (el, ctx) => {
        const socials = socialsFromSettings(ctx.settings);
        if (socials.length === 0) return null;
        const style = elementStyleFrom(el.style);
        const size = typeof el.props?.size === 'number' ? (el.props.size as number) : 20;
        return (
            <div className="sf-layout-social" style={{ display: 'flex', alignItems: 'center', gap: '0.75em', color: 'inherit', ...style }}>
                {socials.map((s) => (
                    <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} style={{ display: 'inline-flex', color: 'inherit' }}>
                        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" role="img" aria-label={s.label}><path d={SOCIAL_GLYPHS[s.label]} /></svg>
                    </a>
                ))}
            </div>
        );
    },

    contact: (el, ctx) => {
        const { footerContactDetails, footerEmail, footerPhone } = ctx.settings;
        if (!footerContactDetails && !footerEmail && !footerPhone) return null;
        const style = elementStyleFrom(el.style);
        return (
            <div className="sf-layout-contact" style={{ display: 'flex', flexDirection: 'column', gap: '0.25em', color: 'inherit', ...style }}>
                {footerContactDetails && <div style={{ whiteSpace: 'pre-wrap' }}>{footerContactDetails}</div>}
                {footerEmail && <a href={`mailto:${footerEmail}`} style={{ color: 'inherit' }}>{footerEmail}</a>}
                {footerPhone && <a href={`tel:${footerPhone}`} style={{ color: 'inherit' }}>{footerPhone}</a>}
            </div>
        );
    },

    // AC-945 / S2.2 — stateful elements are host-rendered and passed in as slots (absent -> nothing).
    search: (_el, ctx) => ctx.slots?.search ?? null,
    cart: (_el, ctx) => ctx.slots?.cart ?? null,
    account: (_el, ctx) => ctx.slots?.account ?? null,
};

function renderElement(el: ILayoutElement, ctx: LayoutRenderContext): React.ReactNode {
    const render = REGISTRY[el.type];
    return render ? render(el, ctx) : null;
}

// --- Renderer ------------------------------------------------------------------------------------
const Column: React.FC<{ col: ILayoutColumn; ctx: LayoutRenderContext }> = ({ col, ctx }) => (
    <div
        className="sf-layout-col"
        style={{ display: 'flex', flexDirection: 'row', alignItems: alignItems(col.vAlign) ?? 'center', justifyContent: justify(col.align) ?? 'flex-start', gap: col.gap ?? 8, minWidth: 0, ...elementStyleFrom(col.style) }}
    >
        {(col.elements ?? []).map((el, i) => <React.Fragment key={el.id ?? i}>{renderElement(el, ctx)}</React.Fragment>)}
    </div>
);

const Row: React.FC<{ row: ILayoutRow; ctx: LayoutRenderContext }> = ({ row, ctx }) => {
    const cols = (row.columns ?? []).filter((c) => !(ctx.isNarrow && c.hideOnMobile));
    // Auto-stack: below the breakpoint every column collapses into a single column.
    const gridTemplateColumns = ctx.isNarrow ? '1fr' : cols.map((c) => track(c.width)).join(' ') || '1fr';
    return (
        <div
            className="sf-layout-row"
            style={{ display: 'grid', gridTemplateColumns, gap: row.gap ?? 12, alignItems: alignItems(row.vAlign) ?? 'center', justifyContent: justify(row.align), ...elementStyleFrom(row.style) }}
        >
            {cols.map((col, i) => <Column key={i} col={col} ctx={ctx} />)}
        </div>
    );
};

// AC-957 / S4.4 — responsive resolution. Below the breakpoint the layout can either auto-stack
// (default / strategy 'stack') or render an entirely separate authored set of rows (strategy
// 'custom' + mobile.rows). A per-layout `mobile.breakpoint` overrides the host's 700px narrow gate;
// when it's absent we keep using ctx.isNarrow so nothing changes for existing layouts.
const LayoutRenderer: React.FC<{ layout: ILayout; ctx: LayoutRenderContext }> = ({ layout, ctx }) => {
    const customBreakpoint = layout.mobile?.breakpoint;
    // Hook is always called (stable order); its value is only used when a custom breakpoint is set.
    const breakpointNarrow = useIsNarrow(customBreakpoint ?? 700);
    const isNarrow = customBreakpoint ? breakpointNarrow : ctx.isNarrow;

    const useCustomMobile = isNarrow && layout.mobile?.strategy === 'custom' && (layout.mobile.rows?.length ?? 0) > 0;
    const rows = useCustomMobile ? (layout.mobile?.rows ?? []) : (layout.rows ?? []);
    // Authored mobile rows render with their own tracks (isNarrow=false); otherwise the effective
    // narrow flag drives auto-stack + hideOnMobile dropping in Row.
    const rowCtx: LayoutRenderContext = { ...ctx, isNarrow: useCustomMobile ? false : isNarrow };

    return (
        <div className={`sf-layout sf-layout-${ctx.region}`} style={{ width: '100%', ...elementStyleFrom(layout.style) }}>
            <div className="sf-layout-inner" style={layout.maxWidth ? { maxWidth: layout.maxWidth, margin: '0 auto' } : undefined}>
                {rows.map((row, i) => <Row key={i} row={row} ctx={rowCtx} />)}
            </div>
        </div>
    );
};

export default LayoutRenderer;
